import React, {useEffect, useState} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import styles from '../../../styles.module.scss';
import {Text, Dropdown} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import {useBatchedFilteredList} from '../../../../../hooks/useBatchedFilteredList';
import {SearchDropdown} from '../../../../../components/commonOldtoNew';
import List from '../../../../../components/Common/List';
import texts from '../../../texts';
import {highlightText} from '../../../../../_legacy/library/utils/highlightText';
import {reportGeneratorsStore} from '../../../stores/ReportGeneratorsStore';
import {getExplanatoryReports, getRequiredInputs, getShouldExecute} from '../../ReportList/utils';
import {defaultExpReportItem} from '../../../consts';
import {getReports} from '../../../services/service';
import {ExplanatoryReport, ReportColumn} from '../../../types';

const getOptions = (
    reports: priority.netitems.nrest.IReportTitle[],
    chartInputs: ReportColumn[]
): ExplanatoryReport[] => {
    return reports.map(report => {
        return {
            ...report,
            value: report.RootNodeId,
            label: report.Title,
            chartInputs: chartInputs
        };
    });
};

const ExplanatorySelector = observer(({selectedReport, onSelect}) => {
    const {currentReport, chartInfo} = reportGeneratorsStore.currentScreen;
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState<string>();
    const [selectedIndex, setselectedIndex] = useState<number>();
    const [options, setOptions] = useState([]);
    const [reports, setReports] = useState<priority.netitems.nrest.IReportTitle[]>(null);

    const {xAxis, series, bubbleConfig, type} = chartInfo;

    useEffect(() => {
        getReports().then(currentReports => {
            setReports(currentReports);
            getOptionalReports(currentReports);
        });
    }, []);

    useEffect(() => {
        reports && getOptionalReports(reports);
    }, [xAxis, series, bubbleConfig?.category]);

    const getOptionalReports = reports => {
        const requiredInputs = getRequiredInputs(chartInfo, currentReport.columns);
        const shouldExecute = getShouldExecute(type, !!requiredInputs.length);
        if (shouldExecute) {
            const nodeId = currentReport.nodeId;
            const optionalExpReports = getExplanatoryReports({
                reports,
                nodeId,
                requiredInputs
            });
            const chartInputs = currentReport.columns.filter(column => column.Input);
            setOptions([defaultExpReportItem, ...getOptions(optionalExpReports, chartInputs)]);
        } else {
            setOptions([defaultExpReportItem]);
        }
    };

    const indexInList = options?.findIndex(opt => opt.Title === selectedReport);
    const batchSize = indexInList > 100 ? Math.ceil(indexInList / 100) * 100 : 100;

    const {batchedItems: filteredOptions, loadMore} = useBatchedFilteredList(
        options,
        batchSize,
        query ?? ''
    );
    const handleSelectItem = (item: ExplanatoryReport) => {
        const currentSelectedIndex = filteredOptions.findIndex(
            opt => opt.RootNodeId === item.RootNodeId && opt.Title === item.Title
        );
        setIsOpen(false);
        setselectedIndex(item.value ? currentSelectedIndex : -1);
        onSelect(item.value ? item : null);
    };

    const handleClose = () => {
        setIsOpen(false);
        setQuery(null);
    };

    useEffect(() => {
        handleClose();
    }, [currentReport]);

    useEffect(() => {
        setselectedIndex(filteredOptions?.findIndex(opt => opt.Title === selectedReport));
    }, [filteredOptions]);

    const handlePageDown = (index: number) => {
        const lastIndex = filteredOptions.length - 1;
        let nextIndex = Number(index) + 10;
        nextIndex = nextIndex > lastIndex ? lastIndex : nextIndex;
        return Promise.resolve({nextSelected: nextIndex});
    };

    const handlePageUp = selectedIndex => {
        let nextIndex = selectedIndex - 10;
        nextIndex = nextIndex >= 0 ? nextIndex : 0;
        return nextIndex;
    };

    const focsedIndex = query ? 0 : selectedIndex > -1 ? selectedIndex : 0;

    return (
        <SearchDropdown
            openOnFocus
            onOpen={() => setIsOpen(true)}
            focusedIndex={focsedIndex}
            clearOnClose={false}
            closeOnClear={false}
            closeOnBlur={false}
            shouldHandleHomeAndEndPress
            selectedIndex={selectedIndex}
            handlePageDown={handlePageDown}
            handlePageUp={handlePageUp}
            inputProps={{
                value: query ?? chartInfo.explanatoryReport?.Title,
                onChange: setQuery,
                returnFocus: false,
                displayClear: isOpen,
                placeholder: '--',
                autoSelect: true,
                size: 'large',
                componentClasses: styles.searchInput
            }}
            onClose={handleClose}
            circularNavigation
            withDataTestId
        >
            <Dropdown.Content componentClasses={styles.fieldsList}>
                <Dropdown.Section gradient>
                    {filteredOptions?.length > 0 ? (
                        <List
                            onScrollToEnd={loadMore}
                            autoHeight
                            autoHeightMax={200}
                            data={filteredOptions}
                            renderItem={({item, index}) => (
                                <Dropdown.Item
                                    divider={
                                        filteredOptions && index !== filteredOptions.length - 1
                                    }
                                    onClick={() => handleSelectItem(item)}
                                    key={index}
                                    index={index}
                                >
                                    <Text skin="paragraph-2">
                                        {highlightText(item.Title, query, true, false, true)}
                                    </Text>
                                </Dropdown.Item>
                            )}
                        />
                    ) : (
                        <Dropdown.Item disabled>
                            <Text componentClasses={styles.emptState} skin="paragraph-2">
                                {texts.noMatchEmptyResults}
                            </Text>
                        </Dropdown.Item>
                    )}
                </Dropdown.Section>
            </Dropdown.Content>
        </SearchDropdown>
    );
});

export default ExplanatorySelector;
